import {
  SAGEMAKER_SERIES_COLORS,
  MetricsInterval,
} from 'src/containers/metrics/constants';
import { assignSeriesColors } from 'src/containers/metrics/seriesColors';
import {
  MetricsChartData,
  SageMakerSeriesUnit,
  SageMakerTimeseriesResponse,
} from 'src/containers/metrics/types';

import { serializeQueryParameters } from './helpers';
import { monitoringAPI } from './monitoring';
import { getScoringTimeseriesRange } from './scoringHealth';

const UNIT_ORDER: SageMakerSeriesUnit[] = ['count', 'ms', 'percent'];

const UNIT_LABEL: Record<SageMakerSeriesUnit, string> = {
  count: 'Counts',
  ms: 'Latency',
  percent: 'Percent',
};

const toChartTimestamp = (value: number) =>
  value < 1_000_000_000_000 ? value * 1000 : value;

export const fetchSageMakerTimeseriesByEndpoint = async (
  interval: MetricsInterval
): Promise<SageMakerTimeseriesResponse> => {
  const { data } = await monitoringAPI.get<SageMakerTimeseriesResponse>(
    `/sagemaker/timeseries/by-endpoint${serializeQueryParameters(
      getScoringTimeseriesRange(interval)
    )}`
  );

  return data;
};

export const sageMakerTimeseriesToCharts = (
  response: SageMakerTimeseriesResponse,
  interval: MetricsInterval
): MetricsChartData[] => {
  const timestamps = response.timestamps.map(toChartTimestamp);
  const stepMs = response.stepSeconds * 1000;

  return response.endpoints.flatMap((endpoint) => {
    const byUnit = new Map<string, typeof endpoint.series>();

    endpoint.series.forEach((series) => {
      const current = byUnit.get(series.unit) || [];
      current.push(series);
      byUnit.set(series.unit, current);
    });

    const units = [
      ...UNIT_ORDER.filter((unit) => byUnit.has(unit)),
      ...Array.from(byUnit.keys()).filter(
        (unit) => !UNIT_ORDER.includes(unit as SageMakerSeriesUnit)
      ),
    ];

    return units.map((unit) => {
      const items = byUnit.get(unit) || [];
      const colors = assignSeriesColors(items, SAGEMAKER_SERIES_COLORS);

      return {
        id: `${endpoint.targetId}:${unit}`,
        title: `${endpoint.name} · ${
          UNIT_LABEL[unit as SageMakerSeriesUnit] || unit
        } (${endpoint.status})`,
        interval,
        timestamps,
        stepMs,
        series: items.map((item, index) => ({
          key: item.key,
          name: item.name,
          color: colors[index],
          values: item.values,
          stats: {
            mean: item.stats?.mean ?? null,
            last: item.stats?.last ?? null,
            max: item.stats?.max ?? null,
            total: item.stats?.total ?? null,
          },
        })),
      };
    });
  });
};
